import * as THREE from 'three';
import type { DevConfig } from '../../devConfig';
import type { CameraConfig } from '../../cameraConfig';
import type { Sight } from '../../types';

const DEBUG_RENDER_ORDER = 10;

export class DevVisuals {
  private readonly scene: THREE.Scene;
  private readonly isDevMode: boolean;
  private collisionDebugEdges: THREE.Object3D[] = [];
  private sightDebugMarkers: THREE.Object3D[] = [];
  private gridFloor: THREE.GridHelper | null = null;
  private gridCeil: THREE.GridHelper | null = null;
  private orbitCenterMarker: THREE.Mesh | null = null;
  private currentDevConfig: DevConfig | null = null;

  constructor(scene: THREE.Scene, isDevMode: boolean) {
    this.scene = scene;
    this.isDevMode = isDevMode;
  }

  registerCollisionEdges(edges: THREE.Object3D[]): void {
    if (!this.isDevMode || edges.length === 0) {
      return;
    }
    const visible = this.shouldShowCollisionBounds();
    edges.forEach((edge) => {
      edge.visible = visible;
      this.collisionDebugEdges.push(edge);
    });
  }

  addSightDebugMarkers(allSights: Sight[]): void {
    if (!this.isDevMode || allSights.length === 0 || this.sightDebugMarkers.length > 0) {
      return;
    }
    const markerGeometry = new THREE.SphereGeometry(0.02, 12, 12);
    const markerMaterial = new THREE.MeshBasicMaterial({
      color: 0xff00ff,
      transparent: true,
      opacity: 0.85,
      depthWrite: false,
      depthTest: false,
    });

    allSights.forEach((sight) => {
      const position = sight.position;
      if (!position) {
        return;
      }
      const marker = new THREE.Mesh(markerGeometry, markerMaterial);
      marker.name = `sight-marker-${sight.id}`;
      marker.position.set(position.x, position.y, position.z);
      marker.renderOrder = DEBUG_RENDER_ORDER;
      marker.visible = this.shouldShowSightMarkers();
      this.sightDebugMarkers.push(marker);
      this.scene.add(marker);
    });
  }

  updateGridHeights(config: CameraConfig): void {
    if (this.gridFloor) {
      this.gridFloor.position.y = config.clampMinHeight;
    }
    if (this.gridCeil) {
      this.gridCeil.position.y = config.clampMaxHeight;
    }
  }

  applyDevConfig(config: DevConfig): void {
    this.currentDevConfig = config;
    if (!this.isDevMode) {
      return;
    }
    const collisionVisible = this.shouldShowCollisionBounds();
    this.collisionDebugEdges.forEach((edge) => {
      edge.visible = collisionVisible;
    });
    const sightMarkersVisible = this.shouldShowSightMarkers();
    this.sightDebugMarkers.forEach((object) => {
      object.visible = sightMarkersVisible;
    });
    const gridVisible = this.shouldShowGrid();
    if (gridVisible) {
      this.ensureDebugGridHelpers();
    }
    if (this.gridFloor) {
      this.gridFloor.visible = gridVisible;
    }
    if (this.gridCeil) {
      this.gridCeil.visible = gridVisible;
    }
    const orbitMarkerVisible = this.shouldShowOrbitCenterMarker();
    if (orbitMarkerVisible) {
      this.ensureOrbitCenterMarker();
    }
    if (this.orbitCenterMarker) {
      this.orbitCenterMarker.visible = orbitMarkerVisible;
    }
  }

  ensureOrbitCenterMarker(): void {
    if (!this.isDevMode || this.orbitCenterMarker) {
      return;
    }
    const geometry = new THREE.SphereGeometry(0.02, 16, 16);
    const material = new THREE.MeshBasicMaterial({
      color: 0x00ffff,
      transparent: true,
      opacity: 0.85,
      depthTest: false,
      depthWrite: false,
    });
    this.orbitCenterMarker = new THREE.Mesh(geometry, material);
    this.orbitCenterMarker.renderOrder = DEBUG_RENDER_ORDER;
    this.orbitCenterMarker.visible = false;
    this.scene.add(this.orbitCenterMarker);
  }

  setOrbitCenter(target: THREE.Vector3): void {
    if (!this.isDevMode) {
      return;
    }
    this.ensureOrbitCenterMarker();
    if (this.orbitCenterMarker) {
      this.orbitCenterMarker.position.copy(target);
    }
  }

  disposeOrbitCenter(): void {
    if (this.orbitCenterMarker) {
      this.scene.remove(this.orbitCenterMarker);
      this.orbitCenterMarker.geometry.dispose();
      if (Array.isArray(this.orbitCenterMarker.material)) {
        this.orbitCenterMarker.material.forEach((material) => material.dispose());
      } else {
        this.orbitCenterMarker.material.dispose();
      }
      this.orbitCenterMarker = null;
    }
  }

  private shouldShowCollisionBounds(): boolean {
    return Boolean(this.isDevMode && this.currentDevConfig?.showCollisionBounds);
  }

  private shouldShowSightMarkers(): boolean {
    return Boolean(this.isDevMode && this.currentDevConfig?.showSightMarkers);
  }

  private shouldShowGrid(): boolean {
    return Boolean(this.isDevMode && this.currentDevConfig?.showReferenceGrid);
  }

  private shouldShowOrbitCenterMarker(): boolean {
    return Boolean(this.isDevMode && this.currentDevConfig?.showOrbitCenterMarker);
  }

  private ensureDebugGridHelpers(): void {
    if (!this.isDevMode || this.gridFloor || this.gridCeil) {
      return;
    }
    this.gridFloor = new THREE.GridHelper(10, 10);
    this.gridCeil = new THREE.GridHelper(10, 10);
    this.gridFloor.renderOrder = DEBUG_RENDER_ORDER;
    this.gridCeil.renderOrder = DEBUG_RENDER_ORDER;
    this.scene.add(this.gridFloor, this.gridCeil);
  }
}
